<template>
  <div>
    <section class="hero">
      <div class="hero-photo" :style="heroStyle" role="img" :aria-label="hero.title || '山野间的露营生活'"></div>
      <div class="hero-shade"></div>
      <div class="hero-copy">
        <span class="eyebrow light">{{ findContent('home.hero.eyebrow').title }}</span>
        <h1><span>{{ heroTitleLines[0] }}</span><em>{{ heroTitleLines[1] }}</em></h1>
        <p>{{ hero.body || '内外探索，遨游无疆。' }}</p>
        <div class="hero-actions">
          <router-link class="round-link" :to="findContent('home.hero.cta').body || '/story'"><span class="round-link-label">{{ findContent('home.hero.cta').title || '关于品牌' }}</span><span>↗</span></router-link>
          <router-link class="hero-secondary-link" :to="findContent('home.hero.cta.products').body || '/products'">{{ findContent('home.hero.cta.products').title || '产品矩阵' }} ↗</router-link>
          <router-link class="hero-secondary-link" :to="findContent('home.hero.cta.careers').body || '/careers'">{{ findContent('home.hero.cta.careers').title || '加入我们' }} ↗</router-link>
        </div>
      </div>
      <div class="hero-caption"><span>{{ findContent('home.hero.caption').title }}</span><span>{{ findContent('home.hero.caption').body }}</span></div>
    </section>

    <section class="intro-band">
      <span class="eyebrow">{{ findContent('home.intro.eyebrow').title }}</span>
      <h2>{{ intro.title || '把日常，过成一场轻盈的探索。' }}</h2>
      <div><p>{{ intro.body }}</p><router-link class="text-link" :to="findContent('home.intro.cta').body || '/story'">{{ findContent('home.intro.cta').title }} <span>↗</span></router-link></div>
    </section>

    <section class="category-section">
      <div class="section-heading"><div><span class="eyebrow">{{ findContent('home.categories.heading').body }}</span><h2>{{ findContent('home.categories.heading').title }}</h2></div><router-link class="text-link" :to="findContent('home.categories.cta').body || '/products'">{{ findContent('home.categories.cta').title }} ↗</router-link></div>
      <div class="category-grid">
        <router-link v-for="(category, index) in categoryCards" :key="category.key" to="/products" class="category-tile" :class="`category-${index + 1}`">
          <img :src="category.imageUrl || category.fallback" :alt="category.title" loading="lazy"><span class="category-index">0{{ index + 1 }} / 03</span><strong>{{ category.title }}</strong><span class="tile-arrow">↗</span>
        </router-link>
      </div>
    </section>

    <section class="product-section">
      <div class="section-heading"><div><span class="eyebrow">{{ findContent('home.products.heading').body }}</span><h2>{{ findContent('home.products.heading').title }}</h2></div><router-link class="text-link" :to="findContent('home.products.cta').body || '/products'">{{ findContent('home.products.cta').title }} ↗</router-link></div>
      <div v-if="products.length" class="product-grid">
        <article v-for="product in products.slice(0, 4)" :key="product.id" class="product-card">
          <div class="product-image"><img :src="product.imageUrl || fallbackImage" :alt="product.name" loading="lazy"><span>{{ product.purchasePlatform || product.category || 'FIELD PICK' }}</span></div>
          <div class="product-meta"><div><h3>{{ product.name }}</h3><p>{{ product.description }}</p></div><b>¥{{ product.price }}</b></div>
          <a v-if="safeUrl(product.purchaseUrl)" class="buy-link" :href="product.purchaseUrl" target="_blank" rel="noopener noreferrer">{{ product.purchasePlatform ? `去${product.purchasePlatform}购买` : '查看商品' }} ↗</a>
        </article>
      </div>
      <div v-else class="empty-products">{{ findContent('home.products.empty').title }}<router-link :to="findContent('home.products.empty.link').body || '/products'">{{ findContent('home.products.empty.link').title || '浏览产品系列' }} ↗</router-link></div>
    </section>

    <section class="service-band">
      <div><span class="eyebrow light">{{ findContent('home.services.eyebrow').title }}</span><h2>{{ services.title }}</h2><p>{{ services.body }}</p><router-link class="light-link" :to="findContent('home.services.cta').body || '/reservation'">{{ findContent('home.services.cta').title }} ↗</router-link></div>
      <div class="service-list"><router-link v-for="index in 3" :key="index" :to="findContent(`home.service.${index}`).body || '/reservation'"><span>0{{ index }}</span><strong>{{ findContent(`home.service.${index}`).title }}</strong><b>↗</b></router-link></div>
    </section>
  </div>
</template>

<script>
import { getPortal } from '../api'

export default {
  data: () => ({ products: [], contents: [], fallbackImage: 'https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?auto=format&fit=crop&w=900&q=85', categoryFallbacks: [
    'https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?auto=format&fit=crop&w=1100&q=85',
    'https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1100&q=85',
    'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=1100&q=85'
  ] }),
  computed: {
    hero() { return this.findContent('home.hero') },
    heroTitleLines() { const parts = (this.hero.title || '心之所向，步之所致。').split(/[，,]/); return parts.length > 1 ? [`${parts[0]}，`, parts.slice(1).join('，')] : [parts[0], ''] },
    intro() { return this.findContent('home.intro') },
    services() { return this.findContent('home.services') },
    heroStyle() { return this.hero.imageUrl ? { backgroundImage: `url("${this.hero.imageUrl.replace(/["\\]/g, '')}")` } : {} },
    categoryCards() { return ['category.home', 'category.camping', 'category.pet'].map((key, index) => ({ ...this.findContent(key), key, fallback: this.categoryFallbacks[index] })) }
  },
  created() { this.load() },
  methods: {
    async load() { try { const data = await getPortal(); this.products = data.products || []; this.contents = data.contents || [] } catch (error) { this.products = []; this.contents = [] } },
    findContent(key) { return this.contents.find(item => item.contentKey === key) || {} },
    safeUrl(value) { return /^https?:\/\//i.test(value || '') }
  }
}
</script>